import partial from 'lodash/partial';
import { useCallback } from 'react';
import {
  ActiveStopFilters,
  FilterType,
  selectMapFilter,
  useAppSelector,
} from '../../../redux';
import { FilterableStopInfo } from '../../../types';
import { Priority } from '../../../types/enums';
import {
  filterHighestPriorityCurrentStops,
  isCurrentEntity,
  isFutureEntity,
  isPastEntity,
} from '../../../utils';
import { useMapObservationDate } from './mapUrlState';
import { useIsInSearchResultMode } from './useIsInSearchResultMode';

type FilterStopsFn = <TStop extends FilterableStopInfo>(
  stops: ReadonlyArray<TStop>,
) => ReadonlyArray<TStop>;

type TypeAndFilterFn = {
  readonly type: FilterType;
  readonly filterFunction: (stop: FilterableStopInfo) => boolean;
};

function getActiveTimeFilters(
  observationDate: Temporal.PlainDate,
  stopFilters: ActiveStopFilters,
): ReadonlyArray<TypeAndFilterFn> {
  return [
    {
      type: FilterType.ShowFutureStops,
      filterFunction: partial(isFutureEntity, observationDate),
    },
    {
      type: FilterType.ShowCurrentStops,
      filterFunction: partial(isCurrentEntity, observationDate),
    },
    {
      type: FilterType.ShowPastStops,
      filterFunction: partial(isPastEntity, observationDate),
    },
  ].filter(({ type }) => stopFilters[type]);
}

function hasPriority(
  priority: Priority,
  stop: { readonly priority: Priority },
) {
  return stop.priority === priority;
}

function getActivePriorityFilters(
  stopFilters: ActiveStopFilters,
): ReadonlyArray<TypeAndFilterFn> {
  return [
    {
      type: FilterType.ShowStandardStops,
      filterFunction: partial(hasPriority, Priority.Standard),
    },
    {
      type: FilterType.ShowTemporaryStops,
      filterFunction: partial(hasPriority, Priority.Temporary),
    },
    {
      type: FilterType.ShowDraftStops,
      filterFunction: partial(hasPriority, Priority.Draft),
    },
  ].filter(({ type }) => stopFilters[type]);
}

function filterStopsByAnyTimeAndPriority<TStop extends FilterableStopInfo>(
  stops: ReadonlyArray<TStop>,
  observationDate: Temporal.PlainDate,
  stopFilters: ActiveStopFilters,
): Array<TStop> {
  const activeTimeFilters = getActiveTimeFilters(observationDate, stopFilters);
  const activePriorityFilters = getActivePriorityFilters(stopFilters);

  return stops.filter(
    (stop) =>
      activeTimeFilters.some(({ filterFunction }) => filterFunction(stop)) &&
      activePriorityFilters.some(({ filterFunction }) => filterFunction(stop)),
  );
}

function filterStopsByTimeAndPriority<TStop extends FilterableStopInfo>(
  stops: ReadonlyArray<TStop>,
  observationDate: Temporal.PlainDate,
  stopFilters: ActiveStopFilters,
): Array<TStop> {
  // If "Show situation on the selected date" filter is selected,
  // ignore other filters
  if (stopFilters[FilterType.ShowHighestPriorityCurrentStops]) {
    return filterHighestPriorityCurrentStops(stops, observationDate);
  }

  return filterStopsByAnyTimeAndPriority(stops, observationDate, stopFilters);
}

export function useFilterStops(): FilterStopsFn {
  const { stopFilters } = useAppSelector(selectMapFilter);
  const observationDate = useMapObservationDate();

  const isInSearchResultMode = useIsInSearchResultMode();

  return useCallback(
    (stops) => {
      if (isInSearchResultMode) {
        return stops;
      }

      return filterStopsByTimeAndPriority(stops, observationDate, stopFilters);
    },
    [observationDate, stopFilters, isInSearchResultMode],
  );
}
